"use client";

import { Calculator, InfoCircle, KeySquare } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { ConvertDialog } from "@/components/core";
import { StakeCardList } from "@/components/features/staking/StakeCard/StakeCardList";
import { StakeModal } from "@/components/features/staking/StakeModal/StakeModal";
import { MetricGridFallback } from "@/components/features/staking/StakingLottie/MetricGridFallback";
import { YellowBox } from "@/components/features/staking/YellowBox";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import useFetchStakeContractData from "@/hooks/stake/useFetchStakeContractData";
import { useStakedTotals } from "@/hooks/stake/useStakedTotals";
import { Link } from "@/i18n/navigation";

export const MyInvest = () => {
  const t = useTranslations("stake");
  const [stakeModalOpen, setStakeModalOpen] = useState(false);
  const { data, isLoading: isQueryLoading } = useFetchStakeContractData();
  // queryFn returns `{}` early when wagmi reads still pending → isLoading flips
  // false before data is real. Treat absent populated fields as loading too.
  const isLoading = isQueryLoading || !data?.user_stake_list;

  // Fast path for the top metric grid — single getUserStake RPC, doesn't
  // wait for the per-stake calculateReward fan-out below.
  const totals = useStakedTotals();

  return (
    <div className="space-y-4 pb-8">
      {/* Section A: metric grid + reward row */}
      <div className="space-y-3 py-4">
        {/* Block 1: total staked values */}
        <h3 className="text-sm font-bold">{t("myInvest.totalStakedTitle")}</h3>
        <MetricGridFallback
          nftCount={totals.nftCount}
          dnmBalanceWei={totals.totalDnmStakedWei}
          uvmBalanceWei={totals.totalUvmStakedWei}
          isLoading={totals.isLoading}
        />

        {/* Block 2: reward split */}
        <h3 className="text-sm font-bold">{t("myInvest.totalRewardTitle")}</h3>
        <div className="flex rounded-lg overflow-hidden">
          <YellowBox
            label={t("myInvest.dailyReward")}
            value={
              isLoading ? (
                <Skeleton variant="dark" className="h-5 w-24" />
              ) : (
                <span dir="ltr">{data?.total_earning ?? "0.00"} UVM</span>
              )
            }
            className="flex-1 rounded-none"
          />
          <Separator orientation="vertical" className="bg-stroke-light" />
          <YellowBox
            label={t("myInvest.claimableToday")}
            value={
              isLoading ? (
                <Skeleton variant="dark" className="h-5 w-24" />
              ) : (
                <span dir="ltr">
                  {data?.total_claimable_reward ?? "0.00"} UVM
                </span>
              )
            }
            className="flex-1 rounded-none"
          />
        </div>
      </div>

      {/* Section B: Stake CTA + Convert strip */}
      <div className="py-4 space-y-4">
        <p className="text-sm font-normal">{t("myInvest.ctaDescription")}</p>
        <div className="w-full flex items-center justify-center">
          <Button
            variant="solid"
            colors="primary"
            className="px-9"
            startIcon={<KeySquare size={18} />}
            onClick={() => setStakeModalOpen(true)}
          >
            {t("myInvest.stakeButton")}
          </Button>
        </div>
        <div className="border border-stroke-light rounded-md px-4 py-3 flex justify-between items-center">
          <p className="text-xs">{t("myInvest.convertStrip")}</p>
          <ConvertDialog />
        </div>
        {/* Section B: Calculator entry point (Phase 1 — non-functional; Phase 2 wires modal) */}
        <div className="px-4 py-3 space-y-3 flex flex-col items-center">
          <div className="flex items-start gap-2">
            <InfoCircle
              size={18}
              className="text-text-primary shrink-0 mt-0.5"
            />
            <p className="text-xs text-text-primary">
              {t("myInvest.calculatorPromo")}
            </p>
          </div>
          <Button asChild variant="solid" colors="secondary" className="px-10">
            <Link href="/info/investment/calculator">
              <Calculator size={18} />
              {t("myInvest.calculatorButton")}
            </Link>
          </Button>
        </div>
      </div>

      {/* Section C: Stake card list */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold">{t("myInvest.myStakesTitle")}</h3>
          <InfoTooltip>{t("myInvest.myStakesTooltip")}</InfoTooltip>
        </div>
        <StakeCardList
          stakes={data?.user_stake_list ?? []}
          isLoading={isLoading}
        />
      </div>

      <StakeModal open={stakeModalOpen} onOpenChange={setStakeModalOpen} />
    </div>
  );
};
